import { getRouteApi } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { Ord } from 'fp-ts/string';
import {
    OnboardingAuditArea,
    OnboardingAuditField,
    OnboardingAuditResult,
    useRepositorySuspenseQuery,
} from '@mews-commander/data-access';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Container, Spinner, Stack, Table, TableProvider, useTableState } from '@optimus-web/core';
import { AuditName } from './AuditName';
import { TaskStatusTag } from './AuditStatusTag';

const columnHelper = createColumnHelper<OnboardingAuditField>();
const columns = [
    columnHelper.accessor('fieldIdentifier', {
        header: () => <LocalizedText textKey={TextKey.GoLiveAuditsTableColumnIntegration} />,
        cell: ({ getValue }) => <AuditName fieldIdentifier={getValue()} />,
    }),
    columnHelper.accessor('status', {
        header: () => <LocalizedText textKey={TextKey.GoLiveAuditsTableColumnStatus} />,
        cell: ({ getValue }) => <TaskStatusTag status={getValue()} />,
    }),
];

const onlyMarketplace = (audit: OnboardingAuditField) => audit.area === OnboardingAuditArea.Marketplace;

const marketplaceSelect = ({ legalEnvironmentData, kycStatus, ...auditChecks }: OnboardingAuditResult) =>
    pipe(
        auditChecks,
        record.filter(onlyMarketplace),
        record.collect(Ord)((_key, audit) => audit)
    );

const IntegrationRoute = getRouteApi('/_authenticated/$currentEnterpriseId/Settings/GoLiveAudits/Integrations');

export const IntegrationsTab = () => {
    const { currentEnterpriseId } = IntegrationRoute.useParams();
    const [tableState, setTableState] = useTableState<OnboardingAuditField>();

    const { data, isRefetching } = useRepositorySuspenseQuery(
        'enterprises',
        'getOnboardingAudit',
        { enterpriseId: currentEnterpriseId },
        {
            refetchOnWindowFocus: 'always',
            select: marketplaceSelect,
        }
    );

    return (
        <Container>
            <Stack alignment="end">{isRefetching ? <Spinner size="small" /> : null}</Stack>
            <TableProvider columns={columns} state={tableState} onStateChange={setTableState}>
                <Table data={data} amountOfRows={data.length} uniqueRowId="fieldIdentifier" />
            </TableProvider>
        </Container>
    );
};
